import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, combineLatest, finalize, from, map, Observable, of, startWith, switchMap, take, tap } from 'rxjs';
import { AuthorizationService } from '../services/authorization.service';
import * as JwtDecode from 'jwt-decode';
import { StateService } from '../services/state.service';
import { StorageService } from '../services/storage.service';
import { LocalStorageKeys } from '../shared/types/storage';
import { ManagementService } from '../components/management-screen/management.service';
import { Permissions } from '../shared/permissions';
import { AUTH0_ROLE, UserProfileResponse } from '../shared/types/UserProfile';
import { RoutePaths } from '../shared/types/common';

function initializeUserProfile(user: UserProfileResponse, token: string, authorization: AuthorizationService, state: StateService, storage: StorageService, management: ManagementService): Observable<void> {
  if (authorization.profileInitiated) {
    return of(void 0);
  }
  state.loading = true;
  const decodedToken = JwtDecode.jwtDecode(token) as Record<string, any>;
  authorization.permissions = decodedToken['permissions'];

  const { id, email, firstName, lastName, account, role, trialDaysLeft, companyName, subscriptionStatus, trialEndsAt } = user;
  authorization.userProfile = {
    id: id,
    email: email,
    firstName: firstName,
    lastName: lastName,
    role: role,
    account: account?.key,
    trialDaysLeft: trialDaysLeft,
    companyName: companyName,
    subscriptionStatus: subscriptionStatus,
    trialEndsAt: trialEndsAt
  };

  const accounts$ =
    authorization.hasPermissions([Permissions.READ_ACCOUNT])
    ? management.getAccounts({ elaborate: false }).pipe(
        map(result => result.accounts.map(acc => ({ key: acc.accountKey, name: acc.name })))
      )
    : of([{ key: account?.key, name: account?.name }]);

  return accounts$.pipe(
    tap((accounts) => {
      const storedAccount = storage.local.getItem(LocalStorageKeys.ACCOUNT);
      state.accounts = accounts.filter(acc => acc.key !== undefined) as { key: string; name: string }[];

      const accountKeys = accounts.map(acc => acc.key);
      const activeOrg =
        storedAccount && accountKeys.includes(storedAccount)
          ? storedAccount
          : (accountKeys.length ? accountKeys[0] : undefined);
      state.setActiveAccount(activeOrg as string);
    }),
    finalize(() => {
      state.loading = false;
    }),
    map(() => void 0)
  );
}

export const authGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const authorization = inject(AuthorizationService);
  const managementService = inject(ManagementService);
  const stateService = inject(StateService);
  const router = inject(Router);
  const storageService = inject(StorageService);
  const requiredPermissions = route.data?.['permissions'] as string[] || [];
  const redirectTo = route.data?.['redirectTo'];
  const forceLogout = !redirectTo;
  const initialQueryParams = router.parseUrl(router.url).queryParams;

  return combineLatest([
    auth.isAuthenticated$, 
    auth.error$.pipe(startWith(null)),
    auth.appState$.pipe(startWith(null))
  ]).pipe(
    take(1),
    switchMap(([isAuthenticated, error, appState]) => {
      if (error) {
        auth.logout({ logoutParams: { returnTo: window.location.origin } });
        return of(false);
      }
      if (!isAuthenticated) {
        auth.loginWithRedirect({ appState: { target: state.url, queryParams: initialQueryParams }, authorizationParams: { scope: 'openid profile' } });
        return of(false);
      }

      stateService.loading = true;
      return stateService.getMyUser().pipe(
        switchMap((user) =>
          from(auth.getAccessTokenSilently({ cacheMode: 'off' })).pipe(
            switchMap((token) => {
              const shouldCompleteSignup = !user.role || user.role === AUTH0_ROLE.SIGNUP;
              if (shouldCompleteSignup) {
                const qp = appState?.queryParams || initialQueryParams || {};
                router.navigate([RoutePaths.TRIAL], { queryParams: { user: JSON.stringify(user), token: token, ...qp } });
                return of(false);
              }
              return initializeUserProfile(user, token, authorization, stateService, storageService, managementService).pipe(
                map(() => {
                  const hasAccess = authorization.hasPermissions(requiredPermissions);
                  if (!hasAccess) {
                    if (forceLogout) {
                      auth.logout({ logoutParams: { returnTo: window.location.origin } });
                    } else {
                      router.navigateByUrl(redirectTo!);
                    }
                    return false;
                  }
                  return true;
                })
              );
            })
          )
        ),
        catchError((error) => {
          // A 404 from getMyUser means the authenticated identity has no provisioned workspace
          // (e.g. not set up in this environment / id mismatch). Show the workspace-error screen
          // with a clear message instead of bouncing straight back to login.
          if (error?.status === 404) {
            // The workspace-error route is itself guarded, so when it re-runs getMyUser and 404s
            // again, just let it render — otherwise we'd loop.
            if (state.url.startsWith(`/${RoutePaths.WORKSPACE_ERROR}`)) {
              return of(true);
            }
            router.navigate([RoutePaths.WORKSPACE_ERROR]);
            return of(false);
          }
          auth.logout({ logoutParams: { returnTo: window.location.origin } });
          return of(false);
        }),
        finalize(() => {
          stateService.loading = false;
        })
      );
    })
  )
}
